import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress, Dialog } from '@mui/material';
import {
  AddOutlined,
  DeleteOutline,
  EditOutlined,
  ErrorOutline,
  RefreshOutlined,
} from '@mui/icons-material';

import Reveal from '../../atome/motion/reveal';
import { Eyebrow, Shimmer } from '../../atome/motion/motion.styled';
import ProductForm from './productForm';
import Orders from './orders';
import { useAuth } from '../../../context/authContext';
import { RootState } from '../../../redux';
import {
  productsFailed,
  productsLoading,
  removeProduct,
  setProducts,
  upsertProduct,
} from '../../../redux/product';
import { ProductServices, productBullets } from '../../../api/product';
import { resolveImage } from '../../../api/axiosIntance';
import { ProductModel, ProductPayload } from '../../models/product/product';
import {
  Badge,
  BadgeRow,
  Bullets,
  Card,
  CardActions,
  CardBody,
  CardTop,
  CountPill,
  DialogBody,
  DialogFoot,
  DialogHead,
  ErrorBanner,
  Grid,
  GhostButton,
  IconAction,
  NoticeBanner,
  Panel,
  PriceRow,
  PrimaryButton,
  Shell,
  Tab,
  TabRail,
  Thumb,
  Toolbar,
  TopBar,
  Wrapper,
  dialogPaperSx,
} from './admin.styled';

/** How long a "saved" / "removed" confirmation stays on screen. */
const NOTICE_TIMEOUT_MS = 4500;

const CatalogueSkeleton = () => (
  <Grid>
    {[0, 1, 2].map((i) => (
      <Card key={i}>
        <Shimmer $height="84px" />
        <Shimmer $height="14px" />
        <Shimmer $height="14px" />
      </Card>
    ))}
  </Grid>
);

/** Sold out / running low / healthy, read straight off the stock count. */
const stockTone = (stock: number) => {
  if (stock <= 0) return 'danger' as const;
  if (stock <= 5) return 'warning' as const;
  return 'forest' as const;
};

/**
 * Admin console. Guarded by ProtectedRoute(requireAdmin).
 * Everything the shop owner needs: add a product, edit its price, description,
 * discount and quantity in stock, or remove it altogether.
 */
const Admin = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { items, status, error } = useSelector((state: RootState) => state.product);

  const [tab, setTab] = useState<'products' | 'orders'>('products');
  const [formOpen, setFormOpen] = useState(false);
  /** null while creating, a product while editing. */
  const [editing, setEditing] = useState<ProductModel | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ProductModel | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [notice, setNotice] = useState('');
  const [actionError, setActionError] = useState('');

  const loadProducts = useCallback(async () => {
    dispatch(productsLoading());
    try {
      dispatch(setProducts(await ProductServices.getProducts()));
    } catch (err: any) {
      dispatch(productsFailed(err?.message || 'Failed to load products'));
    }
  }, [dispatch]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // The confirmation has done its job after a few seconds. Errors stay put —
  // those need reading. The timer restarts on every new notice.
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(''), NOTICE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (product: ProductModel) => {
    setEditing(product);
    setFormOpen(true);
  };

  /** Shared by create and edit — the form only knows it hands us a payload. */
  const handleSave = async (payload: ProductPayload) => {
    setActionError('');
    setNotice('');
    const saved = editing
      ? await ProductServices.updateProduct(editing.id, payload)
      : await ProductServices.createProduct(payload);

    dispatch(upsertProduct(saved));
    setFormOpen(false);
    setEditing(null);
    setNotice(editing ? `"${saved.name}" updated.` : `"${saved.name}" added to the shop.`);
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setActionError('');
    setNotice('');
    setDeleting(true);
    try {
      await ProductServices.deleteProduct(pendingDelete.id);
      dispatch(removeProduct(pendingDelete.id));
      setNotice(`"${pendingDelete.name}" removed from the shop.`);
      setPendingDelete(null);
    } catch (err: any) {
      setActionError(err?.message || 'Could not remove this product.');
    } finally {
      setDeleting(false);
    }
  };

  const isFirstLoad = status === 'loading' && items.length === 0;
  const failedWithNothing = status === 'error' && items.length === 0;

  const renderCatalogue = () => {
    if (isFirstLoad) return <CatalogueSkeleton />;

    if (failedWithNothing) {
      return (
        <Panel>
          <h3>We could not reach the shop</h3>
          <p>{error || 'Something went wrong while loading your products.'}</p>
          <GhostButton type="button" onClick={loadProducts}>
            <RefreshOutlined sx={{ fontSize: 17 }} />
            Try again
          </GhostButton>
        </Panel>
      );
    }

    if (items.length === 0) {
      return (
        <Panel>
          <h3>Nothing on the shelf yet</h3>
          <p>Add your first product and it goes live on the shop straight away.</p>
          <PrimaryButton type="button" onClick={openCreate}>
            <AddOutlined sx={{ fontSize: 18 }} />
            Add a product
          </PrimaryButton>
        </Panel>
      );
    }

    return (
      <Grid>
        {items.map((product) => {
          const cover = resolveImage(product.imageUrl[0]);
          const bullets = productBullets(product.description);
          const hasDiscount = product.discount > 0;

          return (
            <Card key={product.id}>
              <CardTop>
                <Thumb>
                  {cover ? <img src={cover} alt={product.name} /> : <span>K</span>}
                </Thumb>

                <CardBody>
                  <h3 title={product.name}>{product.name}</h3>
                  <small>{product.category || 'Uncategorised'}</small>

                  <PriceRow>
                    <strong>
                      ${(hasDiscount ? product.discountedPrice : product.price).toFixed(2)}
                    </strong>
                    {hasDiscount && <del>${product.price.toFixed(2)}</del>}
                  </PriceRow>

                  <BadgeRow>
                    <Badge $tone={stockTone(product.stock)}>
                      {product.stock > 0 ? `${product.stock} in stock` : 'Sold out'}
                    </Badge>
                    {hasDiscount && <Badge $tone="sand">-{product.discount}%</Badge>}
                  </BadgeRow>
                </CardBody>
              </CardTop>

              {bullets.length > 0 && (
                <Bullets>
                  {bullets.slice(0, 3).map((line, i) => (
                    <li key={i} title={line}>
                      {line}
                    </li>
                  ))}
                </Bullets>
              )}

              <CardActions>
                <IconAction type="button" onClick={() => openEdit(product)}>
                  <EditOutlined sx={{ fontSize: 16 }} />
                  Edit
                </IconAction>
                <IconAction type="button" $danger onClick={() => setPendingDelete(product)}>
                  <DeleteOutline sx={{ fontSize: 16 }} />
                  Remove
                </IconAction>
              </CardActions>
            </Card>
          );
        })}
      </Grid>
    );
  };

  return (
    <Wrapper>
      <Shell>
        <Reveal from="up">
          <TopBar>
            <div>
              <Eyebrow>Klarity</Eyebrow>
              <h1>{tab === 'products' ? 'Products' : 'Orders'}</h1>
              {user?.email && <p>Signed in as {user.email}</p>}
            </div>
          </TopBar>
        </Reveal>

        <TabRail>
          <Tab type="button" $active={tab === 'products'} onClick={() => setTab('products')}>
            Products
          </Tab>
          <Tab type="button" $active={tab === 'orders'} onClick={() => setTab('orders')}>
            Orders
          </Tab>
        </TabRail>

        {tab === 'orders' ? (
          <Orders />
        ) : (
          <>
            {actionError && (
              <ErrorBanner role="alert">
                <ErrorOutline fontSize="small" />
                {actionError}
              </ErrorBanner>
            )}

            {notice && !actionError && <NoticeBanner role="status">{notice}</NoticeBanner>}

            <Toolbar>
              <CountPill>
                {items.length} {items.length === 1 ? 'product' : 'products'}
              </CountPill>

              <PrimaryButton type="button" onClick={openCreate}>
                <AddOutlined sx={{ fontSize: 18 }} />
                Add product
              </PrimaryButton>
            </Toolbar>

            {renderCatalogue()}
          </>
        )}
      </Shell>

      <ProductForm
        open={formOpen}
        product={editing}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSave={handleSave}
      />

      <Dialog
        open={Boolean(pendingDelete)}
        onClose={() => !deleting && setPendingDelete(null)}
        fullWidth
        maxWidth="xs"
        slotProps={{ paper: { sx: dialogPaperSx } }}
      >
        <DialogHead>
          <Eyebrow>Klarity</Eyebrow>
          <h2>Remove product</h2>
        </DialogHead>
        <DialogBody>
          <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: 1.7 }}>
            &ldquo;{pendingDelete?.name}&rdquo; will be taken off the shop. This cannot be
            undone.
          </p>
        </DialogBody>
        <DialogFoot>
          <GhostButton type="button" onClick={() => setPendingDelete(null)} disabled={deleting}>
            Keep it
          </GhostButton>
          <PrimaryButton type="button" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={17} sx={{ color: '#fff' }} /> : 'Remove'}
          </PrimaryButton>
        </DialogFoot>
      </Dialog>
    </Wrapper>
  );
};

export default Admin;
